import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface SectionProps {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  /** Drops the surrounding card, for stacking sections inside an existing panel. */
  bare?: boolean;
  className?: string;
}

/** A labelled block of a settings panel: heading, body, and an optional footer bar. */
export function Section({
  title,
  description,
  actions,
  footer,
  children,
  bare = false,
  className,
}: SectionProps) {
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        bare ? 'py-6 first:pt-0' : 'rounded-lg border border-border bg-background',
        className,
      )}
    >
      <div
        className={cn(
          'flex flex-wrap items-start justify-between gap-x-4 gap-y-2',
          bare ? 'mb-4' : 'px-5 pt-5',
        )}
      >
        <div className="min-w-0">
          <h2 id={headingId} className="text-sm font-medium">
            {title}
          </h2>
          {description && (
            <p className="mt-1 max-w-prose text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>

      <div className={cn(bare ? '' : 'px-5 py-5')}>{children}</div>

      {footer && (
        <div
          className={cn(
            'flex items-center justify-end gap-2 border-t border-border',
            bare ? 'mt-4 pt-4' : 'bg-muted/40 px-5 py-3',
          )}
        >
          {footer}
        </div>
      )}
    </section>
  );
}

/** One label/value line inside a Section — the settings-form workhorse. */
export function SectionRow({
  label,
  hint,
  htmlFor,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'grid gap-x-6 gap-y-1.5 py-3 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]',
        className,
      )}
    >
      <div className="min-w-0">
        <label htmlFor={htmlFor} className="text-sm font-medium">
          {label}
        </label>
        {hint && <p className="mt-0.5 text-sm text-muted-foreground">{hint}</p>}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
